// File: src/pages/reveal/RevealRouteGuard.jsx
import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import axios from "axios";

function authHeaders() {
  const token =
    localStorage.getItem("accessToken") ||
    JSON.parse(localStorage.getItem("tribe_auth") || "{}")?.token || "";
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

// Map server state.kind → desired client route
function routeFromKind(kind) {
  switch (kind) {
    case "match":         return "partner";
    case "ended":         return "portal";
    case "computing":
    case "r1-waiting":
    case "s2-pending":
    case "inmatch-done":
    case "error":
    default:              return "waiting"; // render-in-place
  }
}

export default function RevealRouteGuard({ pageKind }) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const now = Date.now();
        const ROUTE_LOCK_KEY = `reveal_route_lock:${eventId}`;

        // Small lock to avoid rubber-banding
        try {
          const lockUntil = Number(sessionStorage.getItem(ROUTE_LOCK_KEY) || 0);
          if (lockUntil > now) {
            if (alive) setReady(true);
            return;
          }
        } catch {}

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/reveal-mode/${eventId}/state?t=${Date.now()}`,
          { ...authHeaders(), validateStatus: (s) => [200, 202, 204, 410, 404].includes(s) }
        );

        // 404 → do nothing (keep page)
        if (res.status === 404) {
          if (alive) setReady(true);
          return;
        }

        // 204 => server says "waiting/transition": do nothing, keep current page
        if (res.status === 204) {
          if (alive) setReady(true);
          return;
        }

        if (res.status === 410) {
          // Terminal end → portal
          try { sessionStorage.setItem(ROUTE_LOCK_KEY, String(Date.now() + 1000)); } catch {}
          if (pageKind === "portal") {
            // Already on portal: just render; do not re-navigate (prevents white screen).
            console.log("[RouteGuard] 410 on portal → render");
            if (alive) setReady(true);
            return;
          }
          console.log("[RouteGuard] 410 → navigate to portal");
          navigate(`/reveal/${eventId}/portal`, { replace: true, state: { revealExpired: true } });
          return;
        }

        // 200 => follow unified state.kind (fallback to legacy strings)
        if (res.status === 200) {
          const data = res.data || {};
          const rr   = (data?.state && data?.state.kind) ? data.state : null; // unified state object
          const legacyState = typeof data?.state === "string" ? data.state : "";
          const kind = rr?.kind || legacyState;

          // optional route lock directive from server
          const lockMs = Number(data?.clientDirectives?.routeLockMs || 0);
          if (lockMs > 0) {
            try { sessionStorage.setItem(ROUTE_LOCK_KEY, String(Date.now() + lockMs)); } catch {}
          }

          let want = data?.route || routeFromKind(kind);
          const ended = kind === "ended" || legacyState === "ended";

          // ✅ If terminal (ended) and we're already on /portal, just render—don't navigate again.
          if (ended && pageKind === "portal") {
            console.log("[RouteGuard] 200 kind=ended on portal → render");
            if (alive) setReady(true);
            return;
          }

          // If server wants "portal" but hasn't truly ended, treat as waiting
          if ((want === "portal" && !ended) || want === "waiting" || !want) {
            if (alive) setReady(true);
            return;
          }

          if (want && want !== pageKind) {
            // Short lock to avoid bounce
            try { sessionStorage.setItem(ROUTE_LOCK_KEY, String(Date.now() + 1000)); } catch {}

            const stage =
              Number.isInteger(data?.stageIndex) ? data.stageIndex :
              (Number.isInteger(rr?.stage) ? rr.stage : null);

            const stageEndsAt =
              rr?.verifyEndAt ||
              rr?.endAt ||
              (Number.isFinite(data?.endMs) ? data.endMs : null) ||
              data?.roundEndAt ||
              null;

            console.log("[RouteGuard] 200 → navigate", { from: pageKind, to: want, stage });
            navigate(`/reveal/${eventId}/${want}`, {
              replace: true,
              state: {
                stage,
                matchId: data?.matchId || null,
                stageEndsAt,
                fromGuard: true,
              },
            });
            return;
          }

          if (alive) setReady(true);
          return;
        }

        // Fail-open
        if (alive) setReady(true);
      } catch (e) {
        console.warn("[RouteGuard] error", e?.message || e);
        // Fail-open: render current page
        if (alive) setReady(true);
      }
    })();

    return () => { alive = false; };
  }, [eventId, pageKind, navigate]);

  if (!ready) return null;
  return <Outlet />;
}
